/**
 * Placement helpers for the results-map hover/tap card (MapTooltip).
 * All coordinates are viewport (client) coordinates; the caller converts to the card's offset parent.
 */

/** Minimum gap between the card and the edges of the visible viewport. */
export const TOOLTIP_MARGIN = 8;
const CURSOR_OFFSET_X = 14;
const CURSOR_OFFSET_Y = 10;
// Touch: the card sits above the fingertip (or below if there is no room), never under it.
const TOUCH_GAP_ABOVE = 18;
const TOUCH_GAP_BELOW = 28;

/** The part of the page the user can actually see, in client coordinates (handles pinch zoom and mobile URL bars). */
export const getVisibleBounds = () => {
  const vv = window.visualViewport;
  if (vv) {
    return { left: vv.offsetLeft, top: vv.offsetTop, right: vv.offsetLeft + vv.width, bottom: vv.offsetTop + vv.height };
  }
  return { left: 0, top: 0, right: document.documentElement.clientWidth || window.innerWidth, bottom: window.innerHeight };
};

// If the card is bigger than the room, favor the top-left corner (lo) so the heading stays visible.
const clamp = (value, lo, hi) => Math.max(lo, Math.min(value, hi));

/**
 * Where to put a card of `width` x `height` for an anchor point (x, y) so it stays fully inside `bounds`.
 * Mouse: right of and slightly above the cursor, flipped to the left / upward at the edges.
 * Touch: horizontally centered on the tap, above it, or below it when there is no room above.
 */
export const placeTooltip = ({ x, y, width, height, bounds, touch }) => {
  const minLeft = bounds.left + TOOLTIP_MARGIN;
  const maxLeft = bounds.right - TOOLTIP_MARGIN - width;
  const minTop = bounds.top + TOOLTIP_MARGIN;
  const maxTop = bounds.bottom - TOOLTIP_MARGIN - height;

  let left;
  let top;
  if (touch) {
    left = x - width / 2;
    const above = y - TOUCH_GAP_ABOVE - height;
    const below = y + TOUCH_GAP_BELOW;
    if (above >= minTop) top = above;
    else if (below <= maxTop) top = below;
    else top = y - minTop >= bounds.bottom - TOOLTIP_MARGIN - y ? minTop : maxTop;
  } else {
    left = x + CURSOR_OFFSET_X;
    if (left > maxLeft) left = x - CURSOR_OFFSET_X - width;
    top = y - CURSOR_OFFSET_Y;
    if (top > maxTop) top = y + CURSOR_OFFSET_Y - height;
  }
  return { left: clamp(left, minLeft, maxLeft), top: clamp(top, minTop, maxTop) };
};
